'use client';

import { Icon, IconTypes } from '@/react/components/core-ui/Icon';
import {
  useMediaPlayerActions,
  useMediaPlayerState
} from '@/react/components/core-ui/Video/MediaPlayer';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { msg } from '@/services/isomorphic/I18NService';
import { memo, useCallback, type FC } from 'react';
import { ProgressIndicator } from '../../ProgressIndicator';

import S from './styles.module.scss';
import { content_video_pause } from "@/lang/__generated__/ahnu/content_video_pause";
import { content_video_play } from "@/lang/__generated__/ahnu/content_video_play";

interface IPlaybackButtonProps {
  /**
   * Whether to show the playback progress. If left `undefined`,
   * the value will be taken from the UI configuration.
   */
  showProgress?: boolean;
}

/** A button that toggles between play and pause for some media. */
export const PlaybackButton: FC<IPlaybackButtonProps> = ({ showProgress }) => {
  const { isPlaying, currentTime, duration } = useMediaPlayerState();
  const { play, pause } = useMediaPlayerActions();

  const togglePlayback = useCallback((): void => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, pause, play]);

  const showPlaybackProgress =
    showProgress ??
    ConfigurationService.getConfig('ui').getSetting(
      'video.showPlaybackProgress'
    ).value;

  const progress = duration > 0 ? currentTime / duration : 0;

  return (
    <button
      type="button"
      className={S.button}
      onClick={togglePlayback}
      aria-label={
        isPlaying ? msg(content_video_pause) : msg(content_video_play)
      }
    >
      {showPlaybackProgress && <ProgressIndicator progress={progress} />}
      {isPlaying ? (
        <PauseIcon className={showPlaybackProgress ? S.reduceSize : ''} />
      ) : (
        <PlayIcon className={showPlaybackProgress ? S.reduceSize : ''} />
      )}
    </button>
  );
};

/** Props for the playback icons. */
interface IPlaybackIconProps {
  /** Additional classNames for the icon. */
  className?: string;
}

/** A clickable pause icon. */
const PauseIcon = memo(function PauseIcon({ className }: IPlaybackIconProps) {
  return <Icon className={className} icon={IconTypes.Pause} />;
});

/** A clickable play icon. */
const PlayIcon = memo(function PlayIcon({ className }: IPlaybackIconProps) {
  return <Icon className={className} icon={IconTypes.Play} />;
});
